
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Languages, Check } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { toast } from "@/hooks/use-toast";

const LanguageSection = () => {
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  // Update selected language when current language changes
  useEffect(() => {
    setSelectedLanguage(currentLanguage);
  }, [currentLanguage]);

  // Handle language selection (don't apply immediately)
  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    console.log('Language selected:', language);
  };

  // Apply language change
  const handleApplyLanguage = () => {
    changeLanguage(selectedLanguage);
    toast({
      title: t('language_applied'),
      description: `Language changed to ${getLanguageName(selectedLanguage)}`,
    });
  };

  const getLanguageName = (code: string) => {
    const languages: { [key: string]: string } = {
      en: "English",
      es: "Español", 
      fr: "Français",
      de: "Deutsch",
      he: "עברית",
      it: "Italiano",
      pl: "Polski",
      pt: "Português",
      ru: "Русский",
      zh: "中文",
      ja: "日本語",
      ko: "한국어",
      ar: "العربية",
      hi: "हिन्दी"
    };
    return languages[code] || code;
  };

  // Check if language has changed
  const hasLanguageChanged = selectedLanguage !== currentLanguage;

  return (
    <Card className="bg-gray-900/80 border-gray-600">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-ice-white">
          <Languages className="w-5 h-5 text-pink-500" />
          {t('language_preferences')}
        </CardTitle>
        <CardDescription className="text-gray-300">
          {t('language_description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="language-select" className="text-sm font-medium text-ice-white">
              {t('interface_language')}
            </Label>
            <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
              <SelectTrigger id="language-select" className="w-full bg-gray-800 border-gray-600 text-ice-white">
                <SelectValue placeholder="Select a language" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                <SelectItem value="en" className="text-ice-white hover:bg-gray-700">🇺🇸 English</SelectItem>
                <SelectItem value="es" className="text-ice-white hover:bg-gray-700">🇪🇸 Español</SelectItem>
                <SelectItem value="fr" className="text-ice-white hover:bg-gray-700">🇫🇷 Français</SelectItem>
                <SelectItem value="de" className="text-ice-white hover:bg-gray-700">🇩🇪 Deutsch</SelectItem>
                <SelectItem value="he" className="text-ice-white hover:bg-gray-700">🇮🇱 עברית</SelectItem>
                <SelectItem value="it" className="text-ice-white hover:bg-gray-700">🇮🇹 Italiano</SelectItem>
                <SelectItem value="pl" className="text-ice-white hover:bg-gray-700">🇵🇱 Polski</SelectItem>
                <SelectItem value="pt" className="text-ice-white hover:bg-gray-700">🇵🇹 Português</SelectItem>
                <SelectItem value="ru" className="text-ice-white hover:bg-gray-700">🇷🇺 Русский</SelectItem>
                <SelectItem value="zh" className="text-ice-white hover:bg-gray-700">🇨🇳 中文</SelectItem>
                <SelectItem value="ja" className="text-ice-white hover:bg-gray-700">🇯🇵 日本語</SelectItem>
                <SelectItem value="ko" className="text-ice-white hover:bg-gray-700">🇰🇷 한국어</SelectItem>
                <SelectItem value="ar" className="text-ice-white hover:bg-gray-700">🇸🇦 العربية</SelectItem>
                <SelectItem value="hi" className="text-ice-white hover:bg-gray-700">🇮🇳 हिन्दी</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {hasLanguageChanged && (
            <div className="space-y-2">
              <Button 
                onClick={handleApplyLanguage}
                className="w-full bg-pink-500 hover:bg-pink-600"
              >
                <Check className="w-4 h-4 mr-2" />
                {t('apply_language')}
              </Button>
              <p className="text-xs text-amber-400">
                ⚠️ Language will be applied immediately
              </p>
            </div>
           )}
           
           {!hasLanguageChanged && (
             <p className="text-xs text-gray-400">
               Current language: {getLanguageName(currentLanguage)}
             </p>
           )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LanguageSection;
